import { clioDevPortal } from "./clio-dev-portal";
import { js2xml } from "xml-js";
import { NavObjectType } from "../navigationTypes";
import { Contact, Matter, PracticeArea, ResponsibleAttorney, TokenInfo } from "./types";

export namespace ClioService {
    const CLIENT_ID_CLIO = clioDevPortal.credentials.clientId;
    const CLIENT_SECRET_CLIO = clioDevPortal.credentials.clientSecret;
    const CLIO_ORIGIN = "https://app.clio.com";
    // US origin url: https://app.clio.com
    // Canada origin url: https://ca.app.clio.com
    const PROXY_URL = window.location.protocol + "//" + window.location.host + "/browserapi/proxy";

    /*=============================== Token ===============================*/

    /** Gets the Clio auth code from Clio login window
    *  @return {string} auth code if login success, null if login failed
    */
    export async function getAuthCode() : Promise<string>{
        const REDIRECT_URI =  encodeURIComponent(window.location.origin + "/rplus/clio-auth.asp");
        const STATE = getRandomString(6);
        const LOGIN_URL = CLIO_ORIGIN + "/oauth/authorize?response_type=code&client_id=" + CLIENT_ID_CLIO + "&redirect_uri=" + REDIRECT_URI + "&state=" + STATE;
        const PARAMS = "width=600, height=600, left=150, top=100";
        let loginWindowClio =  window.open(LOGIN_URL, "Clio Login", PARAMS);
        
        // wait for Clio login window to close
        const TOTLE_WAIT = 600000;    // wait for 10 minutes
        const EACH_WAIT = 1000;    // wait for 1s each time
        let counter = TOTLE_WAIT / EACH_WAIT;
        while (counter > 0) {
            if (loginWindowClio.closed === true) {
                break;
            } else {
                await delay(EACH_WAIT);
            }
            counter--;
        }
        
        const AUTH_CODE = localStorage.getItem("authCodeClio");
        const RES_STATE = localStorage.getItem("authStateClio");

        if (RES_STATE && AUTH_CODE) {
            if (RES_STATE === STATE) {
                return AUTH_CODE;
            } else {
                console.error("Clio State Mismatch");
                alert("Login Failed due to Clio state mismatch. Please try again.");
                return null;
            }
        } else {
            console.error("Clio Auth Failed");
            alert("Login Failed. Please try again.");
            return null;
        }
    }

    /** Get Clio access & refresh token from auth code
    *  @return {TokenInfo} token info if success, null if failed
    */
    export async function getTokenFromAuthCode(authCode? : string) : Promise<TokenInfo>{
        const SESSION_ID = getSessionID();
        const AUTH_CODE_LOCAL = localStorage.getItem("authCodeClio");
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        } else if (!authCode) {
            if (!AUTH_CODE_LOCAL) {
                console.error("authCode is null or empty");
                return null;
            } else {
                var authCode = AUTH_CODE_LOCAL;
            }
        }

        async function getTokenFromAuthCode(authCode: string, sessionID: string): Promise<TokenInfo> {
            return new Promise((resolve, reject) => {
                const REDIRECT_URI =  window.location.origin + "/rplus" + "/clio-auth.asp";
                const TOKEN_URL = CLIO_ORIGIN + "/oauth/token?grant_type=authorization_code&client_id=" + CLIENT_ID_CLIO + "&client_secret=" + CLIENT_SECRET_CLIO + "&code=" + authCode + "&redirect_uri=" + REDIRECT_URI;

                let params = {
                    "method": "POST",
                    "mode": "cors" as RequestMode,
                    "headers": {
                        "Content-Type": "application/x-www-form-urlencoded"
                    },
                    "body": "",
                    "manageSession": false,
                    "sessionID": sessionID,
                    "url": TOKEN_URL
                };

                let tokenJson;
                let tokenRequest = new XMLHttpRequest();
                tokenRequest.open("POST", PROXY_URL, true);
                tokenRequest.setRequestHeader("Content-Type", "application/json");

                tokenRequest.onreadystatechange = function () {
                    if (this.readyState === 4 && this.status === 200) {
                        try {
                            if (this.responseText !== "") {
                                tokenJson = JSON.parse(JSON.parse(this.responseText).result.response);
                            } else {
                                console.error("Empty Clio response received");
                                reject(new Error("Empty Clio response received"));
                            }
                        } catch (e) {
                            console.error("Invalid Clio JSON received", e);
                            reject(new Error("Invalid Clio JSON received"));
                        }

                        if (tokenJson) {     
                            localStorage.setItem("accessTokenClio", tokenJson.access_token);
                            localStorage.setItem("refreshTokenClio", tokenJson.refresh_token);
                            localStorage.setItem("tokenTypeClio", tokenJson.token_type);

                            // calculate access token expire time
                            let accessTokenExpireTime = new Date();
                            accessTokenExpireTime.setSeconds(accessTokenExpireTime.getSeconds() + tokenJson.expires_in);
                            localStorage.setItem("accessTokenExpireTimeClio", accessTokenExpireTime.toString());

                            let tokenInfo : TokenInfo = {
                                access_token: tokenJson.access_token,
                                refresh_token: tokenJson.refresh_token,
                                token_type: tokenJson.token_type,
                                refresh_token_expireat: accessTokenExpireTime.getTime()
                            };

                            resolve(tokenInfo);
                        }
                    }
                };
                tokenRequest.send(JSON.stringify(params));
            });
        }

        let tokenInfo: TokenInfo | null = null;
        try {
            tokenInfo = await getTokenFromAuthCode(authCode, SESSION_ID);
        } catch (error) {
            console.error("getTokenFromAuthCode Error:", error);
        }

        localStorage.removeItem("authCodeClio");
        localStorage.removeItem("authStateClio");

        return tokenInfo;
    }

    /** Get token info from refresh token and update access token expire time
    *  @return {TokenInfo} token info if success, null if failed
    */
    async function getTokenFromRefreshToken(refreshToken?: string) : Promise<TokenInfo> {
        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        const REFRESH_TOKEN_LOCAL = localStorage.getItem("refreshTokenClio");
        if (!refreshToken) {
            if (!REFRESH_TOKEN_LOCAL) {
                console.error("refreshToken is null or empty");
                return null;
            } else {
                var refreshToken = REFRESH_TOKEN_LOCAL;
            }
        }

        async function getTokenFromRefreshToken(refreshToken: string, sessionID : string) : Promise<TokenInfo> {
            return new Promise((resolve, reject) => {
                const GRANT_TYPE = "refresh_token";
                const TOKEN_URL = CLIO_ORIGIN + "/oauth/token?grant_type=authorization_code&client_id=" + CLIENT_ID_CLIO + "&client_secret=" + CLIENT_SECRET_CLIO + "&grant_type=" + GRANT_TYPE + "&refresh_token=" + refreshToken;

                let params = {
                    "method": "POST",
                    "mode": "cors" as RequestMode,
                    "headers": {
                        "Content-Type": "application/x-www-form-urlencoded"
                    },
                    "body": "",
                    "manageSession": false,
                    "sessionID": sessionID,
                    "url": TOKEN_URL
                };

                let tokenJson;
                let tokenRequest = new XMLHttpRequest();
                tokenRequest.open("POST", PROXY_URL, true);
                tokenRequest.setRequestHeader("Content-Type", "application/json");

                tokenRequest.onreadystatechange = function () {
                    if (this.readyState === 4 && this.status === 200) {
                        try {
                            if (this.responseText !== "") {
                                tokenJson = JSON.parse(JSON.parse(this.responseText).result.response);
                            } else {
                                console.error("Empty Clio response received");
                                reject(new Error("Empty Clio response received"));
                            }
                        } catch (e) {
                            console.error("Invalid Clio JSON received", e);
                            reject(new Error("Invalid Clio JSON received"));
                        }

                        if (tokenJson) {     
                            localStorage.setItem("accessTokenClio", tokenJson.access_token);
                            localStorage.setItem("tokenTypeClio", tokenJson.token_type);

                            // update access token expire time
                            let accessTokenExpireTime = new Date();
                            accessTokenExpireTime.setSeconds(accessTokenExpireTime.getSeconds() + tokenJson.expires_in);
                            localStorage.setItem("accessTokenExpireTimeClio", accessTokenExpireTime.toString());

                            let tokenInfo : TokenInfo = {
                                access_token: tokenJson.access_token,
                                refresh_token: refreshToken,
                                token_type: tokenJson.token_type,
                                refresh_token_expireat: accessTokenExpireTime.getTime()
                            };

                            resolve(tokenInfo);
                        }
                    }
                };
                tokenRequest.send(JSON.stringify(params));
            });
        }

        let tokenInfo : TokenInfo | null = null;
        try {
            tokenInfo = await getTokenFromRefreshToken(refreshToken, SESSION_ID);
        } catch (error) {
            console.error("getTokenFromRefreshToken Error:", error);
        }

        return tokenInfo;
    }

    /** Get full token info in every possible way
    *  @param {boolean} enableReLogin - default false, set true if re-login allowed
    *  @param {boolean} enableTokenHardCheck - default false, set true if allowing sending validate request to check if access token is active
    *  @return {TokenInfo} token info if success, null if failed, in which case client have to mannually re-login
    */
    export async function getTokenInfo(enableReLogin = false, enableTokenHardCheck = false, refreshToken? : string, authCode? : string) : Promise<TokenInfo> {
        try {
            const SESSION_ID = getSessionID();
            if (!SESSION_ID) {
                throw new Error("sessionID is null or empty");
            }

            const REFRESH_TOKEN_LOCAL = localStorage.getItem("refreshTokenClio");
            if (!refreshToken && REFRESH_TOKEN_LOCAL) {
                var refreshToken = REFRESH_TOKEN_LOCAL;
            }

            let tokenInfo : TokenInfo = null;
            if (isAccessTokenValid()) {
                if (!enableTokenHardCheck || isAccessTokenActive()) {
                    tokenInfo = {
                        access_token: localStorage.getItem("accessTokenClio"),
                        refresh_token: localStorage.getItem("refreshTokenClio"),
                        token_type: localStorage.getItem("tokenTypeClio"),
                        refresh_token_expireat: new Date(localStorage.getItem("accessTokenExpireTimeClio")).getTime()
                    };
                    return tokenInfo;
                }
            } else if (refreshToken) {
                // No valid access token exists -> Refresh access token
                tokenInfo = await getTokenFromRefreshToken(refreshToken);

                if (tokenInfo.access_token && isAccessTokenValid(tokenInfo.access_token) && (!enableTokenHardCheck || isAccessTokenActive())) {
                    return tokenInfo;
                } else {
                    // retry connecting to Clio for 3 times to get new access token
                    let retryCounter = 3;
                    while (retryCounter > 0) {
                        tokenInfo = await getTokenFromRefreshToken(refreshToken);
                        if (tokenInfo.access_token && isAccessTokenValid(tokenInfo.access_token) && (!enableTokenHardCheck || isAccessTokenActive())) {
                            return tokenInfo;
                        }

                        retryCounter--;
                    }
                }
            } else if (authCode) {
                // If auth code input exists
                tokenInfo = await getTokenFromAuthCode(authCode);
                if (tokenInfo?.access_token && isAccessTokenValid(tokenInfo.access_token) && (!enableTokenHardCheck || isAccessTokenActive())) {
                    return tokenInfo;
                }
            }

            if (enableReLogin) {
                // Unable to refresh access token -> Re-login if enabled
                const AUTH_CODE = await getAuthCode();
                if (!AUTH_CODE && !SESSION_ID) {
                    tokenInfo = await getTokenFromAuthCode(AUTH_CODE);
                    if (tokenInfo?.access_token && isAccessTokenValid(tokenInfo.access_token) && (!enableTokenHardCheck || isAccessTokenActive())) {
                        return tokenInfo;
                    }
                }
            }
        } catch (error) {
            console.error("getTokenInfo Error:", error);
        }

        return null;
    }

    /** Check if access token is valid
    *  This is a quick soft check via checking if local access token expire time is valid
    */
    function isAccessTokenValid(accessTokenExpireTime? : string) : boolean {
        if (accessTokenExpireTime && !isNaN(Date.parse(accessTokenExpireTime)) &&  new Date(accessTokenExpireTime) > new Date()) {
            return true;
        } else if (new Date(localStorage.getItem("accessTokenExpireTimeClio")) > new Date()) {
            return true;
        }

        return false;
    }

    /** Check if access token is active
    *  This is a hard check via sending validate request to Clio
    *  @return {boolean} true if active, false if inactive, null if session id, token or type missing 
    */
    async function isAccessTokenActive(accessToken? : string) : Promise<boolean>{
        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        const ACCESS_TOKEN_LOCAL = localStorage.getItem("accessTokenClio");
        if (!accessToken) {
            if (!ACCESS_TOKEN_LOCAL) {
                return false;
            } else {
                var accessToken = ACCESS_TOKEN_LOCAL;
            }
        }

        async function validateAccessToken(accessToken : string, sessionID : string, tokenType = "Bearer") : Promise<boolean> {
            return new Promise((resolve, reject) => {
                const VALIDATE_URL = CLIO_ORIGIN + "/api/v4/users/who_am_i";
                const AUTHORIZATION = tokenType + " " + accessToken;

                let params = {
                    "method": "GET",
                    "mode": "cors" as RequestMode,
                    "headers": {
                        "Authorization": AUTHORIZATION
                    },
                    "manageSession": false,
                    "sessionID": sessionID,
                    "url": VALIDATE_URL
                };

                let validateRequest = new XMLHttpRequest();
                validateRequest.open("POST", PROXY_URL, true);
                validateRequest.setRequestHeader("Content-Type", "application/json");

                validateRequest.onreadystatechange = function () {
                    if (this.readyState === 4) {
                        if (this.status === 200) {
                            try {
                                let validateJson = JSON.parse(this.responseText);
                                if (validateJson?.ok) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            } catch (e) {
                                reject(new Error("Invalid Clio JSON received"));
                            }
                        } else {
                            reject(new Error(`Received HTTP ${this.status}`));
                        }
                    }
                };

                validateRequest.send(JSON.stringify(params));
            });
        }
        
        let isValid: boolean | null = null;
        try {
            isValid = await validateAccessToken(accessToken, SESSION_ID);
        } catch (error) {
            console.error("validateAccessToken Error:", error);
            isValid = null;
        }

        return isValid;
    }

    /** Deauthorize access token
    */
    export async function deauthorizeAccessToken(accessToken? : string) : Promise<void> {
        if (!isAccessTokenValid() || !isAccessTokenActive(accessToken)) {
            localStorage.removeItem("accessTokenClio");
            localStorage.removeItem("accessTokenExpireTimeClio");
            localStorage.removeItem("tokenTypeClio");
        } else {
            const SESSION_ID = getSessionID();
            if (!SESSION_ID) {
                throw new Error("sessionID is null or empty");
            }
        
            const ACCESS_TOKEN_LOCAL = localStorage.getItem("accessTokenClio");
            if (!accessToken) {
                if (!ACCESS_TOKEN_LOCAL) {
                    console.error("accessToken is null or empty in local.");
                    return;
                } else {
                    var accessToken = ACCESS_TOKEN_LOCAL;
                }
            }

            async function deauthorizeAccessToken(accessToken: string, sessionID : string, tokenType = "Bearer") : Promise<void> {
                return new Promise((resolve, reject) => {
                    const TOKEN_URL = CLIO_ORIGIN + "/oauth/deauthorize?token=" + accessToken;
        
                    let params = {
                        "method": "POST",
                        "mode": "cors" as RequestMode,
                        "headers": {
                            "Content-Type": "application/x-www-form-urlencoded",
                            "Authorization": tokenType + " " + accessToken
                        },
                        "body": "",
                        "manageSession": false,
                        "sessionID": sessionID,
                        "url": TOKEN_URL
                    };
        
                    let tokenRequest = new XMLHttpRequest();
                    tokenRequest.open("POST", PROXY_URL, true);
                    tokenRequest.setRequestHeader("Content-Type", "application/json");
        
                    tokenRequest.onreadystatechange = function () {
                        if (this.readyState === 4) {
                            if (this.status === 200) {
                                console.log("deauthorizeAccessToken successful");
                                resolve();
                            } else {
                                console.error("deauthorizeAccessToken failed");
                                reject();
                            } 
                        }
                    };
                    tokenRequest.send(JSON.stringify(params));
                });
            }

            try {
                await deauthorizeAccessToken(accessToken, SESSION_ID);
                localStorage.removeItem("accessTokenClio");
                localStorage.removeItem("accessTokenExpireTimeClio");
                localStorage.removeItem("tokenTypeClio");
            } catch (error) {
                console.error("deauthorizeAccessToken Error:", error);
            }
        }

        return;
    }

    export async function deauthorizeAllTokens() : Promise<void> {
        await deauthorizeAccessToken();
        localStorage.removeItem("refreshTokenClio");
        console.log("deauthorizeAllTokens successful");

        return;
    }

    /*=============================== Matter  ===============================*/

    /** Get all matters with allowed practice area (Conveyance) from Clio
    */
    async function getAllMatters(tokenInfo? : TokenInfo) : Promise<Matter[]> {
        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        if (!tokenInfo?.access_token || !tokenInfo?.token_type) {
            tokenInfo = await getTokenInfo(true, true);
        }

        const MATTER_URL = CLIO_ORIGIN + "/api/v4/matters.json?fields=id,display_number,responsible_attorney{id,name,first_name,last_name,initials,email,phone_number},practice_area{id,name},close_date,client{id,name,first_name,middle_name,last_name,type,date_of_birth,primary_email_address,secondary_email_address,primary_phone_number,secondary_phone_number}";

        async function fetchAllPageMatters(url: string, matters: Matter[] = [], sessionID: string, accessToken: string, tokenType = "Bearer"): Promise<Matter[]> {
            return new Promise<Matter[]>((resolve, reject) => {
                const params = {
                    method: "GET",
                    mode: "cors" as RequestMode,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": tokenType + " " + accessToken
                    },
                    manageSession: false,
                    sessionID: sessionID,
                    url: url
                };

                let matterRequest = new XMLHttpRequest();
                matterRequest.open("POST", PROXY_URL, true);
                matterRequest.setRequestHeader("Content-Type", "application/json");
                matterRequest.onreadystatechange = function () {
                    if (this.readyState === 4) {
                        if (this.status === 200) {
                            try {
                                if (!this.responseText) {
                                    reject(new Error("fetchAllPageMatters Empty Clio response received in proxy"));
                                    return;
                                }
                                const parsedProxyRes = JSON.parse(this.responseText);
                                const clioResText = parsedProxyRes.result?.response;
                                if (!clioResText) {
                                    reject(new Error("fetchAllPageMatters Empty Clio response received"));
                                    return;
                                }

                                const clioJson = JSON.parse(clioResText);
                                const matterJson = clioJson.data || [];
                                const matterObjs = parseMattersJson(matterJson);
                                matters.push(...matterObjs);

                                const nextUrl = clioJson.meta?.paging?.next || null;
                                if (nextUrl) {
                                    // Recursively fetch next page (if number of matter result exceeds 200)
                                    fetchAllPageMatters(nextUrl, matters, sessionID, accessToken)
                                        .then((finalMatters) => {
                                            resolve(finalMatters);
                                        })
                                        .catch((e) => reject(e));
                                } else {
                                    // End of pagination
                                    resolve(matters);
                                }
                            } catch (e) {
                                reject(new Error("fetchAllPageMatters Invalid Clio JSON received: " + e.message));
                            }
                        } else {
                            reject(new Error("fetchAllPageMatters HTTP status " + this.status));
                        }
                    }
                };

                matterRequest.send(JSON.stringify(params));
            });
        }

        function parseMattersJson(mattersJson: any): Matter[] {
            if (!mattersJson) {
                return [];
            }

            let matters: Matter[] = [];
            for (let matter of mattersJson) {
                let responsibleAttorneyInfo: ResponsibleAttorney = null;
                if (matter.responsible_attorney?.id && matter.responsible_attorney?.name) {
                    responsibleAttorneyInfo = {
                        clio_id: matter.responsible_attorney.id,
                        name: matter.responsible_attorney.name,
                        first_name: matter.responsible_attorney.first_name,
                        last_name: matter.responsible_attorney.last_name,
                        initials: matter.responsible_attorney.initials,
                        email: matter.responsible_attorney.email,
                        phone_number: matter.responsible_attorney.phone_number
                    };
                }

                let practiceAreaInfo: PracticeArea = null;
                if (matter.practice_area?.id && matter.practice_area.name) {
                    let PSM;
                    switch(matter.practice_area.name) {
                        case "Conveyance (Purchase)":
                            PSM = "P";
                            break;
                        case "Conveyance (Sale)":
                            PSM = "S";
                            break;
                        case "Conveyance (refi)":
                            PSM = "M";
                            break;
                        default:
                            PSM = null;
                            break;
                    }
                    
                    practiceAreaInfo = {
                        id: matter.practice_area.id,
                        name: matter.practice_area.name,
                        PSM: PSM
                    };
                }

                let clientInfo: Contact = {
                    first_name: matter.client?.first_name,
                    middle_name: matter.client?.middle_name,
                    last_name: matter.client?.last_name,
                    name: matter.client?.type === "Company" 
                        ? (matter.client?.name || "") 
                        : [matter.client?.first_name, matter.client?.middle_name, matter.client?.last_name]
                            .filter(part => part != null && part !== "")
                            .join(" "),
                    type: matter.client?.type,
                    id: matter.client?.id,
                    date_of_birth: matter.client?.date_of_birth,
                };

                if (matter.client?.primary_email_address) {
                    clientInfo.email_addresses = [];
                    clientInfo.email_addresses.push({
                        address: matter.client?.primary_email_address,
                        is_default: true
                    });
                }

                if (matter.client?.primary_phone_number) {
                    clientInfo.phone_numbers = [];
                    clientInfo.phone_numbers.push({
                        number: matter.client?.primary_phone_number,
                        is_default: true
                    });
                }

                let matterInfo: Matter = {
                    id: matter.id,
                    display_name: matter.display_number,
                    responsible_attorney: responsibleAttorneyInfo,
                    practice_area: practiceAreaInfo,
                    client: clientInfo,
                    close_date: matter.close_date
                };

                matters.push(matterInfo);
            }

            return matters;
        }

        let matters: Matter[] = [];
        try {
            const allMatters = await fetchAllPageMatters(MATTER_URL, matters, SESSION_ID, tokenInfo.access_token);
        
            // Filter matters with allowed practice area
            matters = allMatters.filter(matter => 
                ["Conveyance (Purchase)", "Conveyance (Sale)", "Conveyance (refi)"]
                    .includes(matter.practice_area?.name)
            );
        } catch (error) {
            console.error("getAllMatters Error:", error);
        }

        return matters;
    }

    /** Get matters with P/S/M practice area only
    */
    export async function getPSMMatters(fileType : string) : Promise<Matter[]> {
        let allMatters = await getAllMatters();
        let purchaseMatters: Matter[] = [];

        for (let matter of allMatters) {
            if (matter.practice_area?.id && matter.practice_area?.name) {
                if (isPSMMatched(matter.practice_area.PSM, fileType)) {
                    purchaseMatters.push(matter);
                }
            }
        }

        return purchaseMatters;
    }

    /** Get matter by matter display name
    */
    export async function getMatterByName(matterName : string) : Promise<Matter> {
        if (!matterName) {
            console.error("matterName is null or empty");
            return null;
        }

        let allMatters = await getAllMatters();
        for (let matter of allMatters) {
            if (matter.display_name === matterName) {
                let clientInfo = await getContactByMatterID(matter.id);
                if (clientInfo) {
                    matter.client = clientInfo;
                }

                return matter;
            }
        }

        return null;
    }

    /*=============================== Client/Contact ===============================*/

    /** Get contact info by matter id
    */
    async function getContactByMatterID(matterID : number, tokenInfo? : TokenInfo) : Promise<Contact> {
        if (!matterID || matterID < 0) {
            console.error("matterID is null or invalid");
            return null;
        }

        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        if (!tokenInfo?.access_token) {
            tokenInfo = await getTokenInfo();
        }

        async function getContactByMatterID(accessToken: string, sessionID : string, matterID: number, tokenType = "Bearer") : Promise<Contact> {
            return new Promise((resolve, reject) => {
                const CONTACT_URL = CLIO_ORIGIN + "/api/v4/matters/" + matterID + "/client.json?fields=id,name,first_name,middle_name,last_name,name,type,prefix,title,initials,primary_email_address,primary_phone_number,date_of_birth,addresses{id,street,city,province,postal_code,country,name,primary},email_addresses{id,address,name,primary},phone_numbers{id,number,name,primary}";

                let params = {
                    "method": "GET",
                    "mode": "cors" as RequestMode,
                    "headers": {
                        "Content-Type": "application/json",
                        "Authorization": tokenType + " " + accessToken
                    },
                    "manageSession": false,
                    "sessionID": sessionID,
                    "url": CONTACT_URL
                };

                let contactJson;
                let contactRequest = new XMLHttpRequest();
                let contactInfo: Contact = null;
                contactRequest.open("POST", PROXY_URL, true);
                contactRequest.setRequestHeader("Content-Type", "application/json");

                contactRequest.onreadystatechange = function () {
                    if (this.readyState === 4 && this.status === 200) {
                        try {
                            if (this.responseText !== "") {
                                contactJson = JSON.parse(JSON.parse(this.responseText).result.response).data;
                            } else {
                                reject(new Error("Empty Clio response received"));
                            }
                        } catch (e) {
                            reject(new Error("Invalid Clio JSON received"));
                        }

                        if (contactJson) {
                            contactInfo = {
                                id: contactJson.id,
                                first_name: contactJson.first_name,
                                middle_name: contactJson.middle_name,
                                last_name: contactJson.last_name,
                                name: contactJson.type === "Company"
                                    ? (contactJson.name || "")
                                    : [contactJson.first_name, contactJson.middle_name, contactJson.last_name]
                                        .filter(part => part != null && part !== "")
                                        .join(" "),
                                type: contactJson.type,
                                date_of_birth: contactJson.date_of_birth,
                                primary_email_address: contactJson.primary_email_address,
                                primary_phone_number: contactJson.primary_phone_number
                            };

                            for (let address of contactJson.addresses) {
                                if (!contactInfo.addresses) {
                                    contactInfo.addresses = [];
                                }
                                contactInfo.addresses.push({
                                    name: address.name,
                                    street: address.street,
                                    city: address.city,
                                    province: address.province,
                                    postal_code: address.postal_code,
                                    country: address.country,
                                    id: address.id,
                                    is_default: address.primary
                                });

                                if (address.primary) {
                                    contactInfo.primary_address = {
                                        name: address.name,
                                        street: address.street,
                                        city: address.city,
                                        province: address.province,
                                        postal_code: address.postal_code,
                                        country: address.country,
                                        id: address.id,
                                        is_default: address.primary
                                    };
                                }
                            }

                            for (let email of contactJson.email_addresses) {
                                if (!contactInfo.email_addresses) {
                                    contactInfo.email_addresses = [];
                                }
                                contactInfo.email_addresses.push({
                                    name: email.name,
                                    address: email.address,
                                    id: email.id,
                                    is_default: email.primary
                                });
                            }

                            for (let phone of contactJson.phone_numbers) {
                                if (!contactInfo.phone_numbers) {
                                    contactInfo.phone_numbers = [];
                                }
                                contactInfo.phone_numbers.push({
                                    name: phone.name,
                                    number: phone.number,
                                    id: phone.id,
                                    is_default: phone.primary
                                });
                            }
                        }

                        resolve(contactInfo);
                    }
                }

                contactRequest.send(JSON.stringify(params));
            });
        }

        let contactInfo: Contact = null;
        try {
            contactInfo = await getContactByMatterID(tokenInfo.access_token, SESSION_ID, matterID);
        } catch (error) {
            console.error("getContactByMatterID Error:", error);
        }

        return contactInfo;
    }

    /*=============================== RPW Data ===============================*/

    /** Post all matter data to create a new file in RPW
    *  XML posting in URL to NewClioDataImport.asp
    *  @param {Matter} matter data from Clio with responsible attorney/lawyer and client info
    *  @return {boolean} true if success, null if input invalid
    */
    export async function postClioMatterToRPW(nav: (frame: NavObjectType) => void, matter: Matter) : Promise<boolean> {
        if (!matter.display_name || !matter.display_name || !matter.practice_area) {
            throw new Error("matter is null or invalid");
        }

        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        let clientAddress = {
            "Street": matter.client.primary_address?.street?? '',
            "City": matter.client.primary_address?.city?? '',
            "Province": matter.client.primary_address?.province?? '',
            "PostalCode": matter.client.primary_address?.postal_code?? '',
            "Country": matter.client.primary_address?.country?? ''
        };

        let clientEmails = [];
        if (matter.client.email_addresses && matter.client.email_addresses.length > 0) {
            for (let email of matter.client.email_addresses) {
                clientEmails.push(email.address);
            }
        }

        let clientPhones = [];
        if (matter.client.phone_numbers && matter.client.phone_numbers.length > 0) {
            for (let phone of matter.client.phone_numbers) {
                let clientPhoneType;
                switch (phone.name) {
                    case "Work":
                        clientPhoneType = "Work Phone";
                        break;
                    case "Home":
                        clientPhoneType = "Home Phone";
                        break;
                    case "Fax":
                        clientPhoneType = "Work Fax";
                        break;
                    case "Other":
                    default:
                        clientPhoneType = "Cell Phone";
                }
                let clientPhoneInfo = {
                    "Number": phone.number,
                    "Type": clientPhoneType
                };
                clientPhones.push(clientPhoneInfo);
            }
        }

        let matterJson = {
            "Matter": {
                "MatterName": matter.display_name,
                "PSM": matter.practice_area.PSM,
                "ClosingDate": matter.close_date,
                "ResponsibleLawyer": {
                    "Name": matter.responsible_attorney?.name?? '',
                    "Initials": matter.responsible_attorney?.initials?? '',
                    "LSUCID": matter.responsible_attorney?.lsucid?? '',
                    "Email": matter.responsible_attorney?.email?? '',
                    "Phone": matter.responsible_attorney?.phone_number?? ''
                },
                "Client": {
                    "FirstName": matter.client.first_name,
                    "MiddleName": matter.client.middle_name,
                    "LastName": (matter.client.type === "Company")? matter.client.name : matter.client.last_name,
                    "Type": matter.client.type,
                    "DateOfBirth": matter.client.date_of_birth,
                    "PrimaryEmail": matter.client.primary_email_address,
                    "PrimaryPhone": matter.client.primary_phone_number,
                    "Address": clientAddress,
                    "Email": clientEmails,
                    "Phone": clientPhones
                }
            }
        };

        let matterXML = js2xml(matterJson, {compact: true});

        const REDIRECT_URL = "lfmaster.asp?formasp=purfilenew&src=" + encodeURIComponent("NewClioDataImport.asp?data=" + encodeURIComponent(matterXML));

        const navigation = {
            component: 'main',
            url: REDIRECT_URL
        }
        nav(navigation);

        return true;
    }

    /** Get full lawyer list of the firm from RPW xml data
    */
    async function getRPWLawyerList() : Promise<ResponsibleAttorney[]> {
        const SESSION_ID = getSessionID();
        if (!SESSION_ID) {
            throw new Error("sessionID is null or empty");
        }

        const APP_SUBCODE = window.location.pathname.split("/")[2];
        if (!APP_SUBCODE) {
            throw new Error("ApplicationSubCode is null or empty");
        }

        let tmpDOM;
        const LAWYER_LIST_URL = window.location.protocol + "//" + window.location.host + "/rplus/" + APP_SUBCODE + "/getthirdpartyinfo.asp?SessionID=" + SESSION_ID;
        await fetch(LAWYER_LIST_URL).then((res) => {
            tmpDOM = res;
        }).catch((error) => {
            console.error("getRPWLawyerList Error:", error);
            tmpDOM = null;
        });

        let resText = await tmpDOM.text();

        let xmlParser = new DOMParser();
        let resXml = xmlParser.parseFromString(resText, "text/xml");

        let lawyerList: ResponsibleAttorney[] = [];
        let lawyerListNode = resXml.querySelector("LawyerList");

        for (let lawyerNode of lawyerListNode.children) {
            let lawyerInfo: ResponsibleAttorney = {
                lsucid: lawyerNode.getAttribute("LSUCID"),
                first_name: lawyerNode.getAttribute("FirstName"),
                last_name: lawyerNode.getAttribute("LastName"),
                name: (lawyerNode.getAttribute("FirstName") + " " + lawyerNode.getAttribute("LastName")).trim(),
            };

            lawyerList.push(lawyerInfo);
        }

        return lawyerList;
    }

    /** Get RPW lawyer with LSUCID if there is a matched one in RPW xml data
    *  @return {ResponsibleAttorney} RPW lawyer info if matched (adding LSUCID), null if not matched
    */
    export async function getRPWLawyer(responsibleAttorney : ResponsibleAttorney) : Promise<ResponsibleAttorney> {
        if (!responsibleAttorney) {
            console.error("responsibleAttorney is null");
            return null;
        }

        let lawyerList = await getRPWLawyerList();
        if (!lawyerList || lawyerList.length === 0) {
            console.error("lawyerList is null or empty");
            return null;
        }

        for (let lawyer of lawyerList) {
            if (lawyer.first_name && lawyer.last_name && lawyer.first_name === responsibleAttorney.first_name && lawyer.last_name === responsibleAttorney.last_name && lawyer.lsucid) {
                responsibleAttorney.lsucid = lawyer.lsucid;
                return responsibleAttorney;
            }
        }

        return null;
    }

    /*=============================== Helper Function ===============================*/

    /** Generates a random string (0-9, a-z, A-Z) of the specified length
    */
    function getRandomString(length: number) : string {
        if (length == null || length < 0)
            return "";

        let randomStr = "";
        const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let counter = 0;
        while (counter < length) {
            randomStr += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
            counter++;
        }

        return randomStr;
    }

    function delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function getSessionID() : string{
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("SessionID")) {
            return urlParams.get("SessionID");
        } else {
            return null;
        }
    }

    /**
    *  Check if the file type of the Clio matter is matched with the PSM file type indicated by user in RPW
    *  @param {string} fileType file type of the Clio matter in PSM
    *  @param {string} PSM file type indicated by user in RPW
    *  @return {boolean} true if matched
    */
    function isPSMMatched(fileType : string, PSM : string) : boolean {
        if (!fileType || !PSM) {
            throw new Error("practiceArea or PSM is null or empty");
        }

        switch(PSM) {
            case "P":
                if (fileType === "P")
                    return true;
                break;
            case "S":
                if (fileType === "S")
                    return true;
                break;
            case "M":
                if (fileType === "M")
                    return true;
                break;
            case "PS":
                if (fileType === "P" || fileType === "S")
                    return true;
                break;
            case "PM":
                if (fileType === "P" || fileType === "M")
                    return true;
                break;
            case "SM":
                if (fileType === "S" || fileType === "M")
                    return true;
                break;
            case "PSM":
                if (fileType === "P" || fileType === "S" || fileType === "M")
                    return true;
                break;
            default:
                console.error("Invalid file type");
                return null;
        }

        return false;
    }
}